import {RouteContext} from "../../RouteContext";
import {
    Button,
    Dialog, DialogActions, DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger, Text, Tree, TreeItem, TreeItemLayout
} from "@fluentui/react-components";
import {MouseEventHandler} from "react";
import {ErrorResponse} from "../../../io/Response";
import {Column, Row} from "./Layout";
import {ClipboardArrowRightFilled} from "@fluentui/react-icons";
import {Errors} from "../../../type/Types";

export type ErrorDialogProps<T extends Error> = RouteContext & Partial<DialogProps> & {
    error?: T
    errorTitle?: (error: T) => string | undefined
    errorMessage?: (error: T) => string | undefined
    onDismiss?: MouseEventHandler<HTMLButtonElement>
}

export default function ErrorDialog<T extends Error>(props: ErrorDialogProps<T>) {
    return <>
        <Dialog
            modalType={'alert'}
            {...props}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>오류</DialogTitle>
                    <DialogContent>
                        <Column gap={'8px'}>
                            {getMessage(props.error, props.errorMessage)}
                            <DebugInfo error={props.error}></DebugInfo>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DebugInfoCopy error={props.error} />
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance={'primary'}
                                onClick={props.onDismiss}>
                                확인
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    </>
}

const defaultMessage = '알 수 없는 오류가 발생했습니다.'

function getMessage<T extends Error>(
    error: T | undefined,
    errorMessage: ((error: T) => string | undefined) | undefined
): string {
    if (error === undefined) {
        return defaultMessage
    }
    if (errorMessage !== undefined) {
        const message = errorMessage(error)
        if (message !== undefined) {
            return message
        }
    }
    if (error instanceof ErrorResponse) {
        console.log(error.typeName)
        return ErrorResponse.WellKnown.description[error.typeName]
    }

    return defaultMessage
}

type DebugInfoProps<T extends Error> = {
    error: T | undefined
}

function DebugInfo<T extends Error>(props: DebugInfoProps<T>) {
    const error = props.error
    if (error === undefined) {
        return <>undefined</>
    }

    return <>
        <Tree aria-label={'Default'}>
            <TreeItem itemType={'branch'}>
                <TreeItemLayout>
                    <Row alignItems={'center'}>
                        <Text>디버그 정보</Text>
                    </Row>
                </TreeItemLayout>
                <Tree>
                    <TreeItem itemType={'leaf'}>
                        <TreeItemLayout>
                            <Row gap={'8px'}>
                                <code>name</code>
                                <code>{error.name}</code>
                            </Row>
                        </TreeItemLayout>
                    </TreeItem>
                    <TreeItem itemType={'leaf'}>
                        <TreeItemLayout>
                            <Row gap={'8px'}>
                                <code>message</code>
                                <code>{error.message}</code>
                            </Row>
                        </TreeItemLayout>
                    </TreeItem>
                    {error instanceof ErrorResponse && <>
                        <TreeItem itemType={'leaf'}>
                            <TreeItemLayout>
                                <Row gap={'8px'}>
                                    <code>typeName</code>
                                    <code>{error.typeName}</code>
                                </Row>
                            </TreeItemLayout>
                        </TreeItem>
                    </>}
                    <TreeItem itemType={'branch'}>
                        <TreeItemLayout>
                            <Row>
                                <code>Call stack</code>
                            </Row>
                        </TreeItemLayout>
                        <Tree>
                            <TreeItem itemType={'leaf'}>
                                <TreeItemLayout>
                                    <code>{error.stack}</code>
                                </TreeItemLayout>
                            </TreeItem>
                        </Tree>
                    </TreeItem>
                </Tree>
            </TreeItem>
        </Tree>
    </>
}

type DebugInfoCopyProps<T extends Error> = DebugInfoProps<T>

function DebugInfoCopy<T extends Error>(props: DebugInfoCopyProps<T>) {
    const error = props.error
    if (error === undefined) {
        return <></>
    }

    return <>
        <Button
            icon={<ClipboardArrowRightFilled />}
            onClick={() => Errors.stringify(error).then(navigator.clipboard.writeText)}>
            디버그 정보 복사
        </Button>
    </>
}